import { useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { deleteScript, downloadScript, type AssetResponse } from '../../api';
import { describeError } from '../../describeError';
import { downloadErrorMessage, saveFile } from '../../useFileDownload';
import { SCRIPT_ACCEPT, scriptProblem, uploadScript } from '../upload/filmScript';

/**
 * A film's script, in the detail rail: download it, and while the film is
 * staged, add, replace or remove it.
 *
 * Optional, so a film without one says so plainly rather than warning — the
 * publish gate does not ask for it. Once published the script is fixed with
 * the film and only the download stays.
 */

interface FilmScriptSectionProps {
  film: AssetResponse;
  /** Staged and the caller may manage assets. The backend refuses anything else. */
  canEdit: boolean;
  /**
   * Holds add, replace and remove while the film is being saved or published:
   * a script confirmed after the publish lands is refused, and the film would
   * stay without it for good. The download is unaffected.
   */
  disabled?: boolean;
  /**
   * Whether a script is being uploaded or removed, so the overlay can hold
   * Publish until it finishes, for the same reason.
   */
  onBusyChange?: (busy: boolean) => void;
}

type Busy = 'uploading' | 'removing' | 'downloading' | null;

export function FilmScriptSection({
  film,
  canEdit,
  disabled = false,
  onBusyChange,
}: FilmScriptSectionProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const filename = film.script_filename;
  const editsLocked = busy !== null || disabled;

  // A download changes nothing, so it does not hold Publish. The cleanup
  // releases Publish if the overlay moves on to another asset mid-change.
  const isChanging = busy === 'uploading' || busy === 'removing';
  useEffect(() => {
    onBusyChange?.(isChanging);
    return () => onBusyChange?.(false);
  }, [isChanging, onBusyChange]);

  // The grid and the overlay both read the film from these, so both show the
  // new script (or its absence) once the write lands.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'asset'] });
  };

  const handlePicked = async (file: File | undefined) => {
    if (!file) return;
    const problem = scriptProblem(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setBusy('uploading');
    try {
      await uploadScript(film.id, file);
      toast.success(filename ? 'Script replaced' : 'Script added');
      refresh();
    } catch (error) {
      toast.error(describeError(error, 'Could not upload the script'));
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    setBusy('removing');
    try {
      await deleteScript(film.id);
      toast.success('Script removed');
      refresh();
    } catch (error) {
      toast.error(describeError(error, 'Could not remove the script'));
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    if (!filename) return;
    setBusy('downloading');
    try {
      saveFile(await downloadScript(film.id, filename));
    } catch (error) {
      toast.error(await downloadErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const buttonClass =
    'shrink-0 rounded px-1.5 py-0.5 text-[11.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:text-slate-300';

  return (
    <section className="flex flex-col gap-2 border-t border-slate-200 pt-3">
      <h3 className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Script</h3>
      {filename ? (
        <div className="flex items-center gap-1.5 text-[12px]">
          <FileSpreadsheet className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2} />
          <span className="min-w-0 flex-1 truncate text-slate-800" title={filename}>
            {filename}
          </span>
          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={busy !== null}
            aria-label={`Download ${filename}`}
            className={`${buttonClass} text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
          >
            {busy === 'downloading' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
            ) : (
              <Download className="h-3.5 w-3.5" strokeWidth={2} />
            )}
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={editsLocked}
                className={`${buttonClass} text-[#0014cc] hover:bg-[#0014cc]/5`}
              >
                {busy === 'uploading' ? 'Uploading…' : 'Replace'}
              </button>
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={editsLocked}
                className={`${buttonClass} text-red-600 hover:bg-red-50`}
              >
                {busy === 'removing' ? 'Removing…' : 'Remove'}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-slate-500">No script</span>
          {canEdit && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={editsLocked}
              className={`${buttonClass} ml-auto text-[#0014cc] hover:bg-[#0014cc]/5`}
            >
              {busy === 'uploading' ? 'Uploading…' : 'Add script'}
            </button>
          )}
        </div>
      )}
      {canEdit && (
        <p className="text-[11px] text-slate-400">
          Optional · one .xlsx, up to 10 MB · fixed once the film is published
        </p>
      )}
      <input
        ref={inputRef}
        data-testid="film-script-input"
        type="file"
        accept={SCRIPT_ACCEPT}
        className="hidden"
        onChange={(event) => {
          void handlePicked(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </section>
  );
}
